import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { Globe } from "lucide-react";
import axios from "axios";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { API } from "../App";

// Language Context
const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    return { language: 'en', setLanguage: () => {}, t: (text) => text };
  }
  return context;
};

// Available languages
const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'ro', name: 'Română', flag: '🇷🇴' },
  { code: 'cy', name: 'Cymraeg', flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
];

// Pre-defined translations for essential UI elements (instant, no API call needed)
const PREDEFINED_TRANSLATIONS = {
  // Romanian (ro)
  'ro_Services': 'Servicii',
  'ro_About': 'Despre',
  'ro_Portfolio': 'Portofoliu',
  'ro_Reviews': 'Recenzii',
  'ro_Contact': 'Contact',
  'ro_Login': 'Conectare',
  'ro_Register': 'Înregistrare',
  'ro_Logout': 'Deconectare',
  'ro_Get Started': 'Începe Acum',
  'ro_Dashboard': 'Panou de Control',
  'ro_Home': 'Acasă',
  'ro_Appointments': 'Programări',
  'ro_Clients': 'Clienți',
  'ro_Workers': 'Angajați',
  'ro_Settings': 'Setări',
  'ro_Quotations': 'Cotații',
  'ro_Invoices': 'Facturi',
  'ro_Blog': 'Blog',
  'ro_Language': 'Limbă',
  'ro_Book Now': 'Rezervă Acum',
  'ro_Call Now': 'Sună Acum',
  'ro_WhatsApp': 'WhatsApp',
  'ro_Save': 'Salvează',
  'ro_Cancel': 'Anulează',
  'ro_Submit': 'Trimite',
  'ro_Edit': 'Editează',
  'ro_Delete': 'Șterge',
  'ro_Add': 'Adaugă',
  'ro_Search': 'Caută',
  'ro_View All': 'Vezi Tot',
  'ro_Loading...': 'Se încarcă...',
  'ro_Welcome': 'Bine ați venit',
  'ro_Welcome back': 'Bine ai revenit',
  'ro_Time Tracking': 'Pontaj',
  'ro_Loyalty': 'Loialitate',
  'ro_Chatbot': 'Chatbot',
  'ro_Total Clients': 'Total Clienți',
  'ro_Total Workers': 'Total Angajați',
  'ro_Total Invoices': 'Total Facturi',
  'ro_Total Revenue': 'Venit Total',
  'ro_Pending': 'În Așteptare',
  'ro_Unpaid': 'Neplătite',
  'ro_From paid invoices': 'Din facturi plătite',
  'ro_Recent Appointments': 'Programări Recente',
  'ro_Profile': 'Profil',
  'ro_Notifications': 'Notificări',
  'ro_Create': 'Creează',
  'ro_Update': 'Actualizează',
  'ro_Close': 'Închide',
  'ro_Back': 'Înapoi',
  'ro_Next': 'Următorul',
  'ro_Previous': 'Anterior',
  'ro_Confirm': 'Confirmă',
  'ro_New Post': 'Postare Nouă',
  'ro_Create Post': 'Creează Postare',
  'ro_Edit Post': 'Editează Postare',
  'ro_Publish': 'Publică',
  'ro_Draft': 'Ciornă',
  'ro_Published': 'Publicat',
  'ro_Create User': 'Creează Utilizator',
  'ro_Change Role': 'Schimbă Rolul',
  'ro_God Admin': 'Administrator Principal',
  'ro_Admin': 'Administrator',
  'ro_Worker': 'Angajat',
  'ro_Client': 'Client',
  
  'ro_Edit Service': 'Editează Serviciul',
  'ro_New Service': 'Serviciu Nou',
  'ro_Create Service': 'Creează Serviciu',
  'ro_Update Service': 'Actualizează Serviciul',
  'ro_Price': 'Preț',
  'ro_Active': 'Activ',
  'ro_Inactive': 'Inactiv',
  'ro_per hour': 'pe oră',
  'ro_fixed price': 'preț fix',
  'ro_per session': 'pe sesiune',
  'ro_Name': 'Nume',
  'ro_Description': 'Descriere',
  'ro_Bank Details': 'Detalii Bancare',
  'ro_Bank Name': 'Numele Băncii',
  'ro_Account Name': 'Numele Contului',
  'ro_Account Number': 'Număr Cont',
  'ro_Sort Code': 'Cod Sortare',
  'ro_Background Music': 'Muzică de Fundal',
  'ro_Upload Music File': 'Încarcă Fișier Muzică',
  'ro_Calendar Sync': 'Sincronizare Calendar',
  'ro_Connect Google Calendar': 'Conectează Google Calendar',
  'ro_Disconnect': 'Deconectează',
  'ro_Connected': 'Conectat',
  'ro_Sync All to Google': 'Sincronizează Tot în Google',
  'ro_Import from Google': 'Importă din Google',
  
  // Welsh (cy)
  'cy_Services': 'Gwasanaethau',
  'cy_About': 'Amdanom',
  'cy_Portfolio': 'Portffolio',
  'cy_Reviews': 'Adolygiadau',
  'cy_Contact': 'Cysylltu',
  'cy_Login': 'Mewngofnodi',
  'cy_Register': 'Cofrestru',
  'cy_Get Started': 'Dechrau Arni',
  'cy_Dashboard': 'Dangosfwrdd',
  'cy_Appointments': 'Apwyntiadau',
  'cy_Clients': 'Cleientiaid',
  'cy_Workers': 'Gweithwyr',
  'cy_Settings': 'Gosodiadau',
  
  // Polish (pl)
  'pl_Services': 'Usługi',
  'pl_About': 'O nas',
  'pl_Portfolio': 'Portfolio',
  'pl_Reviews': 'Opinie',
  'pl_Contact': 'Kontakt',
  'pl_Login': 'Zaloguj się',
  'pl_Register': 'Zarejestruj się',
  'pl_Get Started': 'Rozpocznij',
  'pl_Dashboard': 'Panel',
  'pl_Appointments': 'Wizyty',
  'pl_Clients': 'Klienci',
  'pl_Workers': 'Pracownicy',
  'pl_Settings': 'Ustawienia',
  
  // Spanish (es)
  'es_Services': 'Servicios',
  'es_About': 'Acerca de',
  'es_Portfolio': 'Portafolio',
  'es_Reviews': 'Reseñas',
  'es_Contact': 'Contacto',
  'es_Login': 'Iniciar sesión',
  'es_Register': 'Registrarse',
  'es_Get Started': 'Empezar',
  'es_Dashboard': 'Tablero',
  'es_Appointments': 'Citas',
  'es_Clients': 'Clientes',
  'es_Workers': 'Trabajadores',
  'es_Settings': 'Configuración',
  
  // Portuguese (pt)
  'pt_Services': 'Serviços',
  'pt_About': 'Sobre',
  'pt_Portfolio': 'Portfólio',
  'pt_Reviews': 'Avaliações',
  'pt_Contact': 'Contacto',
  'pt_Login': 'Entrar',
  'pt_Register': 'Registar',
  'pt_Get Started': 'Começar',
  'pt_Dashboard': 'Painel',
  'pt_Appointments': 'Consultas',
  'pt_Clients': 'Clientes',
  'pt_Workers': 'Trabalhadores',
  'pt_Settings': 'Definições',
};

// Translation cache (persisted in localStorage)
const getTranslationCache = () => {
  try {
    const stored = JSON.parse(localStorage.getItem('translation_cache') || '{}');
    // Merge with predefined translations (predefined takes precedence for consistency)
    return { ...stored, ...PREDEFINED_TRANSLATIONS };
  } catch {
    return { ...PREDEFINED_TRANSLATIONS };
  }
};

const setTranslationCache = (cache) => {
  try {
    localStorage.setItem('translation_cache', JSON.stringify(cache));
  } catch {
    // ignore storage errors
  }
};

// Comprehensive list of UI texts for pre-translation
const COMMON_TEXTS = [
  // Navigation & Menu
  'Dashboard', 'Appointments', 'Clients', 'Workers', 'Services', 'Settings',
  'Login', 'Register', 'Logout', 'Home', 'Contact', 'About', 'Profile',
  'Quotations', 'Invoices', 'Time Tracking', 'Blog', 'Loyalty', 'Chatbot',
  'Calendar', 'Reports', 'Notifications', 'Messages', 'Help', 'Language',
  
  // Actions & Buttons
  'Book Now', 'Get Started', 'Learn More', 'Submit', 'Cancel', 'Save',
  'Edit', 'Delete', 'Add', 'Create', 'Update', 'Search', 'Filter', 'View All',
  'Call Now', 'WhatsApp', 'Send', 'Close', 'Back', 'Next', 'Previous',
  'Confirm', 'Apply', 'Reset', 'Clear', 'Upload', 'Download', 'Print',
  'Share', 'Copy', 'Refresh', 'Load More', 'Show More', 'Show Less',
  'Sign In', 'Sign Up', 'Sign Out', 'Forgot Password', 'Reset Password',
  'View Details', 'View All', 'See More', 'Read More', 'Add New',
  
  // Status & States
  'Pending', 'Approved', 'Completed', 'Cancelled', 'Rejected', 'Active',
  'Paid', 'Unpaid', 'Partial', 'Draft', 'Sent', 'Accepted', 'Inactive',
  'Available', 'Unavailable', 'Busy', 'Online', 'Offline', 'Open', 'Closed',
  'Full-time', 'Part-time', 'New', 'Updated', 'Expired', 'Verified',
  
  // Form Labels
  'Email', 'Password', 'First Name', 'Last Name', 'Phone', 'Address',
  'Date', 'Time', 'Price', 'Total', 'Notes', 'Description', 'Status',
  'City', 'Postcode', 'Country', 'Company', 'Subject', 'Message',
  'Start Date', 'End Date', 'Start Time', 'End Time', 'Duration',
  'Amount', 'Quantity', 'Discount', 'Tax', 'Subtotal', 'Grand Total',
  'Payment Method', 'Bank Transfer', 'Cash', 'Card', 'Credit',
  
  // Messages & Notifications
  'Welcome', 'Welcome back', 'Loading...', 'No results', 'Error',
  'Success', 'Please wait', 'Connecting to server...', 'Saving...',
  'Are you sure?', 'This action cannot be undone', 'Confirm deletion',
  'Changes saved successfully', 'Something went wrong', 'Try again',
  'No data available', 'No items found', 'No appointments', 'No clients',
  
  // Homepage Sections
  'Property Professional Services', 'All the help you need for your property',
  'Projects Completed', 'Years Experience', 'Satisfaction Rate', 'Support Available',
  'Our Services', 'About Us', 'Portfolio', 'Reviews', 'Contact Us',
  'Why Choose Us', 'Our Team', 'Testimonials', 'Get in Touch',
  'Quality Service', 'Professional Team', 'Affordable Prices', 'Fast Response',
  
  // Dashboard
  'Total Clients', 'Total Appointments', 'Total Invoices', 'Total Revenue',
  'This Month', 'This Week', 'Today', 'Upcoming', 'Recent Activity',
  'Quick Actions', 'Statistics', 'Overview', 'Summary', 'Total Workers',
  'From paid invoices', 'Recent Appointments', 'Unpaid',
  
  // Client Dashboard
  'Welcome', 'Client Number', 'Book a Service', 'Pay for Services',
  'Upcoming Appointments', 'Past Appointments', 'My Invoices', 'My Rewards',
  'Loyalty Card', 'Loyalty Tiers', 'Request Appointment', 'Leave Feedback',
  'View Invoice', 'Pay Now', 'No appointments', 'No invoices',
  
  // Appointments
  'New Appointment', 'Schedule Appointment', 'Reschedule', 'Cancel Appointment',
  'Appointment Details', 'Client Information', 'Service Details',
  'Assigned Workers', 'Worker Notes', 'Client Notes', 'Admin Notes',
  'Recurring', 'One-time', 'Daily', 'Weekly', 'Monthly', 'Custom',
  
  // Workers
  'Add Worker', 'Worker Details', 'Availability', 'Schedule',
  'Hours Worked', 'Total Hours', 'Completed Jobs', 'Pending Jobs',
  'Set Availability', 'View Availability', 'Clock In', 'Clock Out',
  'Full-time', 'Part-time',
  
  // Invoices & Quotations
  'New Invoice', 'New Quotation', 'Invoice Details', 'Quotation Details',
  'Due Date', 'Issue Date', 'Payment Status', 'Record Payment',
  'Send Invoice', 'Send Quotation', 'Download PDF', 'Mark as Paid',
  'Items', 'Add Item', 'Remove Item', 'Service Name', 'Unit Price',
  
  // Loyalty
  'Loyalty Points', 'Points Balance', 'Redeem Points', 'Earn Points',
  'Available Offers', 'My Rewards', 'Conversion Rate', 'Account Credit',
  
  // Settings
  'General Settings', 'Account Settings', 'Notification Settings',
  'Email Templates', 'Bank Details', 'API Settings', 'Integrations',
  'Save Changes', 'Discard Changes', 'Reset to Default',
  
  // Blog
  'Blog', 'New Post', 'Create Post', 'Edit Post', 'Delete Post',
  'Publish', 'Draft', 'Published', 'No blog posts yet. Create your first post!',
  
  // User Management
  'Create User', 'Edit User', 'Delete User', 'Change Role', 'User Details',
  'God Admin', 'Admin', 'Worker', 'Client', 'Welcome back'
];

// Language Provider Component - Uses FREE Emergent AI Translation
export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    return localStorage.getItem('app_language') || 'en';
  });
  const [translations, setTranslations] = useState(() => getTranslationCache());
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationVersion, setTranslationVersion] = useState(0);

  const setLanguage = (lang) => {
    setLanguageState(lang);
    localStorage.setItem('app_language', lang);
    // Force immediate re-render
    setTranslationVersion(v => v + 1);
  };

  // Translation function - returns cached translation or predefined or original
  const t = useCallback((text) => {
    if (!text || typeof text !== 'string' || language === 'en') return text;
    const cacheKey = `${language}_${text}`;
    // First check runtime cache (from state)
    const cached = translations[cacheKey];
    if (cached) return cached;
    // Then check predefined translations (always available)
    const predefined = PREDEFINED_TRANSLATIONS[cacheKey];
    if (predefined) return predefined;
    // Return original if no translation found
    return text;
  }, [language, translations]);

  // Translate text using FREE Emergent AI (backend endpoint)
  const translateOnDemand = useCallback(async (text) => {
    if (!text || typeof text !== 'string' || language === 'en') return text;
    
    const cacheKey = `${language}_${text}`;
    // Check predefined first
    if (PREDEFINED_TRANSLATIONS[cacheKey]) return PREDEFINED_TRANSLATIONS[cacheKey];
    
    try {
      const response = await axios.post(`${API}/translate`, {
        text,
        target_language: language
      }, { timeout: 10000 });
      
      if (response.data?.translated && response.data.translated !== text) {
        setTranslations(prev => {
          const newTranslations = { ...prev, [cacheKey]: response.data.translated };
          setTranslationCache(newTranslations);
          return newTranslations;
        });
        return response.data.translated;
      }
      return text;
    } catch (error) {
      console.error('Translation error:', error);
      return text;
    }
  }, [language]);

  // Batch translate multiple texts in parallel
  const batchTranslate = useCallback(async (texts) => {
    if (language === 'en') return;
    
    setIsTranslating(true);
    const textsToTranslate = texts.filter(text => {
      const cacheKey = `${language}_${text}`;
      // Skip if already in predefined or cached
      return text && typeof text === 'string' && 
             !PREDEFINED_TRANSLATIONS[cacheKey] && 
             !translations[cacheKey];
    });

    if (textsToTranslate.length === 0) {
      setIsTranslating(false);
      return;
    }

    // Translate in batches of 15 for faster processing
    const batchSize = 15;
    for (let i = 0; i < textsToTranslate.length; i += batchSize) {
      const batch = textsToTranslate.slice(i, i + batchSize);
      await Promise.all(batch.map(text => translateOnDemand(text)));
      // Small delay between batches to avoid rate limiting
      if (i + batchSize < textsToTranslate.length) {
        await new Promise(resolve => setTimeout(resolve, 30));
      }
    }
    setIsTranslating(false);
  }, [language, translations, translateOnDemand]);

  // Pre-translate common UI texts when language changes
  useEffect(() => {
    if (language === 'en') {
      return;
    }
    
    console.log(`Language changed to ${language}, starting translation...`);
    batchTranslate(COMMON_TEXTS);
  }, [language, batchTranslate]);

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage, 
      t, 
      translateOnDemand,
      batchTranslate,
      isTranslating,
      languages: LANGUAGES 
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Auto-translating text component - translates any text automatically
export const TranslatedText = ({ children, as: Component = 'span', className = '' }) => {
  const { language, t, translateOnDemand } = useLanguage();
  const [translatedText, setTranslatedText] = useState(children);

  useEffect(() => {
    if (!children || typeof children !== 'string' || language === 'en') {
      setTranslatedText(children);
      return;
    }

    // Check cache first
    const cached = t(children);
    if (cached !== children) {
      setTranslatedText(cached);
      return;
    }

    // Translate on demand
    translateOnDemand(children).then(result => {
      setTranslatedText(result);
    });
  }, [children, language, t, translateOnDemand]);

  return <Component className={className}>{translatedText}</Component>;
};

// Hook for translating dynamic content (API responses, database content)
export const useTranslatedContent = (content) => {
  const { language, translateOnDemand, t } = useLanguage();
  const [translatedContent, setTranslatedContent] = useState(content);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (language === 'en' || !content) {
      setTranslatedContent(content);
      return;
    }

    const translateContent = async () => {
      setIsLoading(true);
      
      if (typeof content === 'string') {
        const cached = t(content);
        if (cached !== content) {
          setTranslatedContent(cached);
        } else {
          const translated = await translateOnDemand(content);
          setTranslatedContent(translated);
        }
      } else if (Array.isArray(content)) {
        // Translate array of strings
        const translatedArray = await Promise.all(
          content.map(async (item) => {
            if (typeof item === 'string') {
              const cached = t(item);
              return cached !== item ? cached : await translateOnDemand(item);
            }
            return item;
          })
        );
        setTranslatedContent(translatedArray);
      } else if (typeof content === 'object') {
        // Translate object values (useful for API responses)
        const translatedObj = { ...content };
        const keysToTranslate = ['title', 'name', 'description', 'content', 'message', 'label', 'text'];
        
        for (const key of keysToTranslate) {
          if (translatedObj[key] && typeof translatedObj[key] === 'string') {
            const cached = t(translatedObj[key]);
            translatedObj[key] = cached !== translatedObj[key] ? cached : await translateOnDemand(translatedObj[key]);
          }
        }
        setTranslatedContent(translatedObj);
      }
      
      setIsLoading(false);
    };

    translateContent();
  }, [content, language, t, translateOnDemand]);

  return { translatedContent, isLoading };
};

// Helper function to wrap text for translation
export const T = ({ children }) => {
  const { t } = useLanguage();
  if (typeof children !== 'string') return children;
  return t(children);
};

// Language Selector Component - Compact version for navbar
export default function LanguageSelector({ className = "", compact = false }) {
  const { language, setLanguage, languages, isTranslating } = useLanguage();
  const currentLang = languages.find(l => l.code === language) || languages[0];

  return (
    <div className={`flex items-center flex-shrink-0 ${className}`} data-testid="language-selector">
      <Select value={language} onValueChange={setLanguage}>
        <SelectTrigger 
          className={`${compact ? 'w-[52px] h-7 px-1.5' : 'w-[85px] h-8 px-2'} bg-[#121212]/90 border-[#262626] text-white hover:bg-[#1A1A1A] backdrop-blur-sm text-xs`}
          aria-label="Select language"
        >
          <div className="flex items-center gap-1 justify-center">
            <Globe size={compact ? 10 : 12} className={`${isTranslating ? 'animate-spin text-[#0099FF]' : 'text-[#A1A1AA]'} flex-shrink-0`} />
            <span className={compact ? 'text-[10px]' : 'text-xs'}>{currentLang.flag}</span>
          </div>
        </SelectTrigger>
        <SelectContent className="bg-[#121212] border-[#262626] min-w-[130px]">
          {languages.map((lang) => (
            <SelectItem 
              key={lang.code} 
              value={lang.code}
              className="text-white hover:bg-[#1A1A1A] cursor-pointer text-xs"
            >
              <span className="flex items-center gap-2">
                {lang.flag} {lang.name}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
